import {
  eventVatIssues,
  toVatCents,
  validateVatComponents,
  VatComponentIssue,
  VatComponentIssueCode,
  VATComponent,
  VatTaxCountry,
} from 'types/shared/helper/eventVatComponents';

/** The `t` of `useTranslation('calendar')`. */
type Translate = (key: string, options?: Record<string, unknown>) => any;

// Translation keys (namespace "calendar") per validation code; the German
// entries match the shared helper's messages (vatComponentIssueMessage).
const ISSUE_KEYS: Record<VatComponentIssueCode, string> = {
  NOT_A_LIST: 'The VAT split is invalid.',
  EMPTY: 'The VAT split needs at least one component.',
  COUNTRY_MISSING: "A VAT split requires the restaurant's country in the address settings.",
  RATE_NOT_IN_CATALOG: "This VAT rate is not available for the restaurant's country.",
  DUPLICATE_ID: 'A component of the VAT split exists twice.',
  DUPLICATE_TITLE: 'Each component needs its own name.',
  TITLE_MISSING: 'Each component needs a name.',
  AMOUNT_INVALID: 'Amounts of the VAT split must be whole cents from 0 €.',
  SUM_MISMATCH: 'The components must add up to exactly the price.',
  NO_POSITIVE_COMPONENT: 'At least one component must have an amount.',
  NEGATIVE_PRICE: 'A price with a VAT split must not be negative.',
  DECIMAL_RATE_NEEDS_COMPONENTS:
    'This VAT rate is saved as a VAT split with one component; please select it again in the editor.',
  DECIMAL_RATE_IN_SPLIT_EVENT:
    'This event uses VAT splits, so the decimal rate of this option must be saved as a VAT split with one component too; please turn on the split for this option and enter the rate as its only component.',
};

const euroText = (cents: number) => (Math.abs(cents) / 100).toFixed(2).replace('.', ',') + ' €';

const issueMessage = (issue: VatComponentIssue, t: Translate): string => {
  if (issue.code === 'SUM_MISMATCH' && issue.differenceCents) {
    return String(
      issue.differenceCents > 0
        ? t('calendar_The components must add up to exactly the price ({{amount}} still to split).', {
            amount: euroText(issue.differenceCents),
          })
        : t('calendar_The components must add up to exactly the price ({{amount}} split too much).', {
            amount: euroText(issue.differenceCents),
          })
    );
  }
  return String(t(`calendar_${ISSUE_KEYS[issue.code]}`));
};

/**
 * First tax split problem of a whole event, e.g. after a change of the event
 * base price that no option split was redistributed for yet.
 */
export function eventVatSaveError(
  event: Parameters<typeof eventVatIssues>[0],
  country: VatTaxCountry | null,
  previous: Parameters<typeof eventVatIssues>[0] | null = null,
  t: Translate
): string | null {
  const [issue] = eventVatIssues(event, country, { previous });
  if (!issue) return null;
  const values = { name: issue.optionName?.trim() || issue.optionId, message: issueMessage(issue, t) };
  return String(
    issue.kind === 'option'
      ? t('calendar_Option “{{name}}”: {{message}}', values)
      : t('calendar_Add-on “{{name}}”: {{message}}', values)
  );
}

/** Message that blocks saving an option or add-on with an incomplete tax split. */
export function vatSplitSaveError(
  option: { vatComponents?: VATComponent[] | null },
  grossEuros: number,
  country: VatTaxCountry | null,
  pendingPercent: string | null,
  t: Translate
): string | null {
  if (option.vatComponents === undefined || option.vatComponents === null) return null;
  // Already translated by the editor.
  if (pendingPercent) return pendingPercent;
  const [issue] = validateVatComponents(option.vatComponents, toVatCents(grossEuros), country);
  return issue ? issueMessage(issue, t) : null;
}
